import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { DataService } from '@shared/services';
import { DataTableEnum } from '@shared/services/indexdb/enum';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { Observable, of } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';

/**
 * Runtime cache enablement flag, initialized from `environment.isCacheEnabled`.
 * Read by `ApiServiceBase` when building the per-request HTTP context. Can be
 * toggled off explicitly at runtime (e.g. after a middle-office login that
 * invalidates store-scoped cached responses) instead of mutating the global
 * `environment` import.
 */
@Injectable({ providedIn: 'root' })
export class CacheSettingsService {
  readonly enabled = signal(environment.isCacheEnabled);
}

export interface CacheInput {
  // The value is in seconds
  cacheExpirationTime: number;
  forcedKey?: string;
  refresh?: boolean;
  store?: keyof typeof DataTableEnum;
}

export const IS_CACHE_ENABLED = new HttpContextToken<boolean>(() => false);
export const CACHE = new HttpContextToken<CacheInput>(() => ({
  cacheExpirationTime: 0,
  refresh: false,
  store: 'CACHE',
}));

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private readonly dataService = inject(DataService);
  private readonly platformContextService = inject(PlatformContextService);
  private readonly logs = inject(LogsService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.platformContextService.isServer() || !req.context.get(IS_CACHE_ENABLED)) {
      return next.handle(req);
    }

    const { cacheExpirationTime, forcedKey, refresh, store } = req.context.get(CACHE);
    const key = forcedKey ?? req.urlWithParams;

    return this.dataService.get$<unknown>(key, store ?? 'CACHE').pipe(
      mergeMap((value: unknown) => {
        if (value && !refresh) {
          this.logs.debug('CacheInterceptor', `[CACHE] ${req.method} ${req.url}`, { muted: true });
          return of(new HttpResponse<unknown>({ body: value }));
        }

        return next.handle(req).pipe(
          tap((valueLoaded) => {
            if (valueLoaded instanceof HttpResponse) {
              this.dataService.set<unknown>(key, valueLoaded.body, cacheExpirationTime, store ?? 'CACHE').subscribe();
            }
          })
        );
      })
    );
  }
}
